import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';

import { projects as homeFurnitureProjects } from './HomeFurniture';
import { projects as gardenFurnitureProjects } from './GardenFurniture';
import { projects as pergolasAndDecksProjects } from './PergolasAndDecks';

function SearchBar({ homeFurnitureRef, gardenFurnitureRef, pergolasAndDecksRef, ourProjectsRef }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    const searchRef = useRef(null);
    const inputRef = useRef(null);
    const listboxId = "search-results-listbox";

    const searchResults = [
        ...homeFurnitureProjects.map(project => ({ text: project.title, ref: homeFurnitureRef, type: 'project' })),
        ...gardenFurnitureProjects.map(project => ({ text: project.title, ref: gardenFurnitureRef, type: 'project' })),
        ...pergolasAndDecksProjects.map(project => ({ text: project.title, ref: pergolasAndDecksRef, type: 'project' })),
        { text: 'ריהוט בית', ref: homeFurnitureRef, type: 'category' },
        { text: 'ריהוט גן', ref: gardenFurnitureRef, type: 'category' },
        { text: 'פרגולות ודקים', ref: pergolasAndDecksRef, type: 'category' },
        { text: 'פרויקטים שלנו', ref: ourProjectsRef, type: 'category' },
    ];

    const filteredResults = searchTerm
        ? searchResults.filter((result) => {
            const searchLower = searchTerm.toLowerCase().trim();
            const textLower = result.text.toLowerCase();
            if (textLower.startsWith(searchLower)) return true;
            const words = textLower.split(/\s+/);
            return words.some(word => word.startsWith(searchLower));
        })
        : [];

    // עדכון מיקום ריספונסיבי מותאם גם לשינויי חלון ולמקלדות מובייל
    const updatePosition = useCallback(() => {
        if (searchRef.current) {
            const rect = searchRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    }, []);

    useEffect(() => {
        if (isOpen) updatePosition();
    }, [isOpen, updatePosition]);

    useEffect(() => {
        if (!isOpen) return;
        window.addEventListener('scroll', updatePosition, { passive: true });
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);

    // סגירה בלחיצה בחוץ (כולל ניהול נגישות של Focus)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                const dropdownElement = document.getElementById('search-dropdown');
                const noResultsElement = document.getElementById('no-results-dropdown');

                if (
                    (!dropdownElement || !dropdownElement.contains(event.target)) &&
                    (!noResultsElement || !noResultsElement.contains(event.target))
                ) {
                    setIsOpen(false);
                    setFocusedIndex(-1);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearch = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        setIsOpen(term.length > 0);
        setFocusedIndex(-1);
    };

    const handleResultClick = (result) => {
        // מונע קריסה במידה וה-ref לא הועבר או לא מוגדר
        if (result.ref && result.ref.current) {
            result.ref.current.scrollIntoView({ behavior: 'smooth' });
        }
        setSearchTerm('');
        setIsOpen(false);
        setFocusedIndex(-1);
        inputRef.current?.blur();
    };

    const clearSearch = () => {
        setSearchTerm('');
        setIsOpen(false);
        setFocusedIndex(-1);
        inputRef.current?.focus();
    };

    const handleKeyDown = (event) => {
        if (!isOpen || filteredResults.length === 0) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setFocusedIndex(prev => prev < filteredResults.length - 1 ? prev + 1 : 0);
                break;
            case 'ArrowUp':
                event.preventDefault();
                setFocusedIndex(prev => prev > 0 ? prev - 1 : filteredResults.length - 1);
                break;
            case 'Enter':
                event.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredResults.length) {
                    handleResultClick(filteredResults[focusedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setFocusedIndex(-1);
                inputRef.current?.blur();
                break;
            default:
                break;
        }
    };

    const DropdownPortal = () => {
        if (!isOpen || filteredResults.length === 0) return null;

        return createPortal(
            <div
                id="search-dropdown"
                className="absolute z-50 bg-surface rounded-xl shadow-hover border border-secondary
                           max-h-80 overflow-y-auto w-full sm:max-w-2xl"
                style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                }}
            >
                <ul className="py-2" role="listbox" id={listboxId} aria-label="תוצאות חיפוש">
                    {filteredResults.map((result, index) => {
                        const isFocused = focusedIndex === index;
                        return (
                            <li
                                key={index}
                                id={`search-option-${index}`}
                                onClick={() => handleResultClick(result)}
                                className={`
                                    px-4 py-3 cursor-pointer transition-colors duration-200
                                    flex items-center justify-between outline-none
                                    ${isFocused ? 'bg-secondary text-primary font-medium' : 'text-text-secondary hover:bg-secondary/50'}
                                `}
                                role="option"
                                aria-selected={isFocused}
                            >
                                <span className="text-right flex-1">
                                    {result.text}
                                </span>
                                <span className={`
                                    text-xs px-2.5 py-1 rounded-md font-medium mr-3
                                    ${result.type === 'category'
                                    ? 'bg-secondary-dark text-text-primary'
                                    : 'bg-background border border-secondary text-text-secondary'
                                }
                                `}>
                                    {result.type === 'category' ? 'קטגוריה' : 'פרויקט'}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </div>,
            document.body
        );
    };

    const NoResultsPortal = () => {
        if (!isOpen || searchTerm === '' || filteredResults.length > 0) return null;

        return createPortal(
            <div
                id="no-results-dropdown"
                className="absolute z-50 bg-surface rounded-xl shadow-hover border border-secondary
                           py-10 text-center w-full sm:max-w-2xl"
                style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                }}
            >
                <Search className="w-8 h-8 mx-auto mb-3 text-secondary-dark" aria-hidden="true" />
                <p className="text-text-primary font-medium">לא נמצאו תוצאות עבור "{searchTerm}"</p>
                <p className="text-sm text-text-muted mt-1">נסה לחפש במילות מפתח אחרות או דפדף בקטגוריות</p>
            </div>,
            document.body
        );
    };

    return (
        <>
            <div ref={searchRef} className="relative w-full mx-auto" dir="rtl">
                <div className="relative group">
                    {/* אייקון חיפוש */}
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                        <Search className={`w-5 h-5 transition-colors duration-300 ${isOpen ? 'text-primary' : 'text-text-muted'}`} aria-hidden="true" />
                    </div>

                    {/* שדה קלט - עיצוב סולידי ומודרני (Scandi-Craft) */}
                    <input
                        ref={inputRef}
                        type="search"
                        placeholder="איזה פרויקט בעץ תרצו למצוא?"
                        value={searchTerm}
                        onChange={handleSearch}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsOpen(searchTerm.length > 0)}
                        className="
                            w-full py-3.5 px-12 pr-12 pl-12
                            text-base text-text-primary placeholder-text-muted
                            bg-surface border border-secondary-dark rounded-xl
                            shadow-soft transition-all duration-300
                            focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary focus:shadow-hover
                            hover:border-text-muted
                        "
                        aria-label="חיפוש פרויקטים וקטגוריות"
                        aria-expanded={isOpen}
                        aria-autocomplete="list"
                        aria-controls={isOpen ? listboxId : undefined}
                        aria-activedescendant={focusedIndex >= 0 ? `search-option-${focusedIndex}` : undefined}
                        role="combobox"
                    />

                    {/* כפתור ניקוי (נגיש במקלדת) */}
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            className="
                                absolute left-3 top-1/2 transform -translate-y-1/2
                                p-1.5 text-text-muted hover:text-primary hover:bg-secondary
                                rounded-md transition-all duration-200
                                focus:outline-none focus:ring-2 focus:ring-primary-light
                            "
                            aria-label="נקה תוכן חיפוש"
                        >
                            <X className="w-4 h-4" aria-hidden="true" />
                        </button>
                    )}
                </div>
            </div>

            <DropdownPortal />
            <NoResultsPortal />
        </>
    );
}

export default SearchBar;